/**
 * IconDropdown component module.
 * @module shared/components/Dropdown
 *
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Select, {components} from 'react-select';
import debug from 'debug';
import styled, {withTheme, useTheme} from 'styled-components/macro';
import Badge from './Badge';
import Icon from './Icon';
import Popover from './Popover';
import {DescriptiveText} from '../index';
const log = debug('circleblack:IconDropdown');

const _OptionSubLabel = styled.div`
  font-family: ${props => props.theme.labelText.name};
  font-size: ${props => props.theme.labelText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  text-align: left;
  margin-top: 5px;
`;

const _GroupLabel = styled.div`
  font-family: ${props => props.theme.labelText.name};
  font-size: ${props => props.theme.labelText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  text-align: left;
`;

const _GroupDivider = styled.div`
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'};
  height: 1px;
  width: 100%;
  margin-top: 2px;
`;

const _Button = styled.button`
  position: relative;
  border: none;

  &:hover svg #Background {
    opacity: 0.15;
  }
  :focus {
    outline: 0;
  }
;
  background-color: rgba(0, 0, 0, 0);
  cursor: pointer;
`;

const _Relative = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Option = prop => {
  const theme = useTheme();
  return (
    <Popover
          trigger='hover'
          placement='top'
          content={prop.data.disabledHelpText}
          isControlled={!prop.isDisabled}>
      <components.Option {...prop}>
        <_Relative>
          {!!prop.data.icon && <Icon svg={prop.data.icon}
                                     color={prop.isFocused || prop.isSelected ? theme.primaryColor : '#DDD'}
                                     margin={'0 20px 0 0'}
                                     height={'30px'}
                                     width={'30px'}/>}
          <DescriptiveText font={theme.neumorphic ? 'ProximaRegular' : theme.bodyText.name}
                           fontSize={theme.neumorphic ? '18px' : theme.bodyText.size}
                           margin={theme.neumorphic ? '15px 0 15px 0' : '5px' }
                           color={theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'}
                           width={'fill'}
                           opacity={1}
                           alignment={'left'}>{prop.data.label}</DescriptiveText>
          <_OptionSubLabel>{prop.data.subLabel}</_OptionSubLabel>
          {(prop.data.label === 'Notifications')
            ? <Badge
                    diameter='10px'
                    types={['notification']}
                    position={{top: '-5px', right: '10px'}}
                    lastResize={prop.data.lastResize}
                    IsSubsectionBadge={true}/>
            : null}
        </_Relative>
      </components.Option>
    </Popover>
  );
};

const GroupHeading = prop => (
  <components.GroupHeading {...prop}>
    <_GroupLabel>{prop.children}</_GroupLabel>
    <_GroupDivider/>
  </components.GroupHeading>
);

const Group = prop => (
  <div>
    <components.Group {...prop} />
  </div>
);

/**
 * IconDropdown component based on 'react-select' to create dropdown menu
 * @name IconDropdown
 */
const IconDropdown = (props) => {
  const [isOpen, setOpen] = useState(false);
  const {icon, iconWidth, iconHeight, options, currentSelection, lastResize, isDisplayNotificationsBadge, position} = props;

  const [selectedOption, setSelectedOption] = useState();

  useEffect(()=>{
    setSelectedOption(currentSelection);
  }, [currentSelection]);

  const toggleOpen = () => {
    setOpen(!isOpen);
  };

  const buttonClose = () => {
    setOpen(false);
  };

  const onSelectChange = val => {
    if (val.action) {
      if (val.action === 'SUBMIT') {
        document.forms[val.form].dispatchEvent(new Event('submit', {bubbles: true, cancelable: true}));
      } else {
        val.action();
      }
    }
    if (val.value && !props.currentSelection) {
      setSelectedOption(val);
    }
    toggleOpen();
  };

  // noinspection JSUnusedGlobalSymbols
  const styling = {
    control: () => ({
      display: 'none',
    }),
    menu: styles => {
      delete styles.width;

      if (props.theme.neumorphic) {
        styles.maxWidth = '300px';
        styles.width = 'fit-content';
        styles.height = 'fit-content';
      }

      return ({
        ...styles,
        backgroundColor: props.theme.tileBackgroundColor,
        borderRadius: props.theme.neumorphic ? '8px' : '4px',
        borderStyle: 'none',
        boxShadow: '2px 2px 5px 2px rgba(0,0,0,0.5)',
        marginTop: '0px',
        right: '0px',
        whiteSpace: 'nowrap',
        paddingLeft: '1px',
        paddingRight: '1px',
        ...position,
      });
    },
    option: (styles, state) => {
      delete styles[':active'];
      const bgColor = () => {
        if (state.isSelected) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
        }
        if (state.isFocused) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
        }
        if (state.isDisabled) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
        }
        return 'transparent';
      };

      if (state.isDisabled) {
        styles.opacity = '0.5';
      }

      if (props.theme.neumorphic) {
        styles['&:hover'] = {
          borderRadius: '3px',
        };

        if (state.isSelected) {
          styles.borderRadius = '3px';
          styles.boxShadow = '-1px -1px 0px 0px rgba(255, 255, 255, 0.18) inset, '
              + '1px 1px 0px 0px rgba(0, 0, 0, 0.3) inset';
        }
      }
      return {
        ...styles,
        padding: props.theme.neumorphic ? '0 20px 0 10px' : '5px',
        backgroundColor: bgColor(),
        cursor: 'pointer',
      };
    },
    groupHeading: (styles, state) => {
      const headingDisplay = ()=>{
        if (!state.data.label) return 'none';
        return 'block';
      };
      return {
        ...styles,
        display: headingDisplay(),
        padding: '10px 5px 5px 5px',
        textTransform: 'none',
      };
    },
    menuList: styles => ({
      ...styles,
      maxHeight: 'fit-content',
    }),
  };

  log('rendering');
  return <div>
    <_Button id={'gear-menu'} onClick={toggleOpen} onBlur={buttonClose} type={'button'}>
      {(lastResize && isDisplayNotificationsBadge)
        ? <Badge
              diameter='10px'
              types={['notification']}
              position={{top: '10px', right: '2px'}}
              lastResize={lastResize}
              IsSubsectionBadge={false}/>
        : null}
      <Icon svg={icon} margin={'0'} height={iconHeight} width={iconWidth}/>
    </_Button>
    {isOpen ? <div>
      <Select
          autoFocus
          components={{Group, Option, GroupHeading}}
          menuIsOpen
          value = {selectedOption}
          onChange={onSelectChange}
          options={options.secondarySection ? [options.mainSection, options.secondarySection] : [options.mainSection]}
          tabSelectsValue={false}
          styles={{
            ...styling,
            menuPortal: base => ({...base, zIndex: 999}),
          }}
      />
    </div> : null}
  </div>;
};

export default withTheme(IconDropdown);

const optionPropTypes = PropTypes.shape({
  /**
   * Option label
   */
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,

  /**
   * Option sub label
   */
  subLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  /**
   * Option icon
   */
  icon: PropTypes.string,

  /**
   * Whether the option is disabled
   */
  isDisabled: PropTypes.bool,

  /**
   * Help text when hovering over a disabled option
   */
  disabledHelpText: PropTypes.string,

  /**
   * Option action
   */
  action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,

  /**
   * The form ID to submit when action is 'SUBMIT'
   */
  form: PropTypes.string,

  /**
   * Option value
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  /**
   * Last window resize moment (omit to not display badge)
   */
  lastResize: PropTypes.object,
});

IconDropdown.propTypes = {
  /**
   * Set of options.
   */
  options: PropTypes.shape({
    /**
     * Options main section
     */
    mainSection: PropTypes.shape({
      /**
       * Section label
       */
      label: PropTypes.string,

      /**
       * Section options set
       */
      options: PropTypes.arrayOf(optionPropTypes).isRequired,
    }).isRequired,
    /**
     * Options secondary section
     */
    secondarySection: PropTypes.shape({
      /**
       * Section label
       */
      label: PropTypes.string,

      /**
       * Section options set
       */
      options: PropTypes.arrayOf(optionPropTypes).isRequired,
    }),
  }),

  /**
   * Name of the Icon
   */
  icon: PropTypes.string,
  /**
   * The width of the Icon
   */
  iconWidth: PropTypes.string,
  /**
   * The height of the Icon
   */
  iconHeight: PropTypes.string,
  /**
   * Currently selected option
   */
  currentSelection: optionPropTypes,

  /**
   * Whether to display the notifications badge
   */
  isDisplayNotificationsBadge: PropTypes.bool,

  /**
   * Last window resize moment (omit to not display badge)
   */
  lastResize: PropTypes.object,

  /**
   * The theme prop provided by the ThemeProvider
   */
  theme: PropTypes.object,

  /**
   * Absolute position of menu
   */
  position: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

IconDropdown.defaultProps = {
  isDisplayNotificationsBadge: false,
  iconWidth: '40px',
  iconHeight: '40px',
};
